/**
 * `GET /api/listing-meta?listingId=…` — checkout-capability surface.
 *
 * Background — motivating incident (`CUTOVER_STEP_3_REVIEW.md` Finding #7,
 * fixed `cfefcab2`): the route emitted `paypal_capabilities` and the
 * checkout sheets read `paypalCapabilities` → wallet buttons silently
 * disappeared from offer/auction-winner checkouts. Naming was unified to
 * `paypalCapabilities` in PR #253. This schema is what makes that drift
 * compile-fatal next time.
 *
 * Consumers (web only — mobile doesn't read this route):
 *   - `components/offers/offer-checkout-sheet.tsx`
 *   - `components/detailed-listing-view/auction-winner-checkout-sheet.tsx`
 *
 * Field notes:
 *   - `paypalMerchantId` may be `null` for a seller who hasn't connected
 *     PayPal at all — UI gates fall back to `availableProviders`.
 *   - `paypalCapabilities` is the PayPal merchant integrations array
 *     (`CUSTOM_CARD_PROCESSING`, `EXPRESS_CHECKOUT`, etc.) — empty array,
 *     not undefined, when none are granted. Wallet buttons require
 *     `CUSTOM_CARD_PROCESSING` to be ACTIVE.
 *   - `availableProviders` is rail availability per `getAvailableProvidersFor()`
 *     — the gating signal UI MUST use, never derive from `paypalMerchantId`.
 *   - `chatPrivateCancelledAt` is an ISO timestamp string OR null. Surfaces
 *     when a private chat-locked listing was unlocked, so consumers can
 *     decide whether the listing is still in its private window.
 *
 * @see app/api/listing-meta/route.ts — handler
 * @see .tondocs/payments/API_RESPONSE_AUDIT.md §1
 * @see .tondocs/payments/CUTOVER_STEP_3_REVIEW.md Finding #7 — motivating drift
 */

import { z } from "zod";
import { pinned } from "./pin";
import { rawImageRefField, type RawImageRef } from "./raw-image-ref";
import {
  PaymentProviderName,
  PaymentProviderNameSchema,
} from "./payments-seller-provider";
import { ShippingMethod, ShippingMethodSchema } from "./shipping-method";

export interface ListingMetaResponse {
  listingId: string;
  sellerId: string;
  sellerName: string;
  /** `profiles.avatar_url` as stored — resolve before any URL sink. */
  sellerAvatarUrl: RawImageRef | null;
  /** Trimmed; `null` when empty/blank. */
  sellerCheckoutNote: string | null;
  paypalMerchantId: string | null;
  paypalCapabilities: string[];
  availableProviders: PaymentProviderName[];
  shippingMethods: ShippingMethod[];
  /**
   * Shippo white-label discriminator (WS4.4) at the listing level. Drives the
   * checkout sheet's choice between the flat-rate path (`'legacy'` / null) and
   * the address-first live-rate sub-flow (`'live_rate'`). Mirrors
   * `CartListingNested.shipping_mode` (see ./cart.ts) so offer / auction-winner
   * surfaces can flip the same `deriveShippingMode` / `aggregateParcel` logic
   * as cart. DB CHECK constraint enforces these two values exactly.
   *
   * @see ./cart.ts CartListingNested.shipping_mode
   * @see .tondocs/shippo/PLAN.md §K.6 (provider-neutral rename)
   */
  shippingMode: "legacy" | "live_rate" | null;
  /**
   * Aggregated parcel dimensions for the live-rate sub-flow. Populated ONLY
   * when `shippingMode === 'live_rate'` AND every parcel column on the
   * listing row is non-null; otherwise `null` (legacy listing, or live-rate
   * listing with incomplete dims — caller treats as "cannot live-rate quote").
   *
   * Inner field names are plain (no `parcel_` prefix) — they are already
   * nested under `parcel`. Mirrors the per-row columns flattened on
   * `CartListingNested` (`parcel_weight`, `parcel_length`, …) but in nested
   * form for the offer/auction single-listing surfaces.
   *
   * @see ./cart.ts CartListingNested.parcel_*
   */
  /**
   * Post-K.6 (PLAN §A "Parcel units" policy C, locked 2026-06-22) storage is
   * canonical grams + mm. Slice C1 emitted these as constants from the
   * route; slice C2 (2026-06-23) tightens the type to the single canonical
   * `'g'` / `'mm'` literal so consumers can no longer accept the legacy
   * locale variants. Display conversion lives in
   * `lib/shipping/format-parcel.ts`.
   */
  parcel: {
    weight: number;
    length: number;
    width: number;
    height: number;
    mass_unit: "g";
    distance_unit: "mm";
  } | null;
  /** Server-resolved uppercase ISO-4217 (e.g. "USD"). From `listings.currency`. */
  currency: string;
  /** ISO timestamp string or null. */
  chatPrivateCancelledAt: string | null;
}

export const ListingMetaResponseSchema = pinned<ListingMetaResponse>()(
  z.object({
    listingId: z.string(),
    sellerId: z.string(),
    sellerName: z.string(),
    sellerAvatarUrl: rawImageRefField().nullable(),
    sellerCheckoutNote: z.string().nullable(),
    paypalMerchantId: z.string().nullable(),
    paypalCapabilities: z.array(z.string()),
    availableProviders: z.array(PaymentProviderNameSchema),
    shippingMethods: z.array(ShippingMethodSchema),
    shippingMode: z.enum(["legacy", "live_rate"]).nullable(),
    parcel: z
      .object({
        weight: z.number(),
        length: z.number(),
        width: z.number(),
        height: z.number(),
        mass_unit: z.literal("g"),
        distance_unit: z.literal("mm"),
      })
      .nullable(),
    currency: z.string(),
    chatPrivateCancelledAt: z.string().nullable(),
  }),
);
